import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, StatCard, StatusBadge } from "@/components/common/DashboardBits";
import ExportVisitsButton from "@/components/common/ExportVisitsButton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function VisitsPage({ role = "office" }) {
  const { t } = useLang();
  const showLoanLeads = role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/visits").then((r) => { if (alive) setRows(r.data); }).catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, []);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((r) => [r.customer_name, r.bank_name, r.executive_name, r.district, r.pincode].join(" ").toLowerCase().includes(needle))
    : rows;
  const banks = new Set(rows.map((r) => r.bank_name)).size;
  const execs = new Set(rows.map((r) => r.executive_name)).size;

  return (
    <div data-testid="visits-page">
      <PageHeader
        title={t("visits")}
        subtitle="All property visits submitted by field executives"
        right={<ExportVisitsButton variant="default" className="bg-slate-950 hover:bg-slate-800" />}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total visits" value={rows.length} tone="green" testId="visits-total" />
        <StatCard label="Banks / NBFCs" value={banks} tone="blue" testId="visits-banks" />
        <StatCard label="Field executives" value={execs} tone="amber" testId="visits-executives" />
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, bank, executive, district…" className="pl-9" data-testid="visits-search" />
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>#</TableHead>
              <TableHead>Customer / Visit</TableHead>
              <TableHead>Bank / NBFC</TableHead>
              <TableHead>Field Executive</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Visit Date</TableHead>
              <TableHead>Status</TableHead>
              {showLoanLeads && <TableHead className="text-right">Loan Leads</TableHead>}
              <TableHead className="text-right">Photos</TableHead>
              <TableHead className="text-right">Valuation (₹)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.visit_id} data-testid={`visit-row-${r.visit_id}`}>
                  <TableCell className="num text-slate-500">{r.sno}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.customer_name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[240px]">{r.property_address}</div>
                  </TableCell>
                  <TableCell>
                    <div>{r.bank_name}</div>
                    <div className="text-xs text-slate-500">{r.branch}</div>
                  </TableCell>
                  <TableCell>
                    <div>{r.executive_name}</div>
                    <div className="text-xs text-slate-500">{r.executive_phone}</div>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.district}{r.pincode !== "-" ? ` · ${r.pincode}` : ""}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.property_type}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{fmtDate(r.submitted_at)}</TableCell>
                  <TableCell><StatusBadge status={r.lead_status} /></TableCell>
                  {showLoanLeads && <TableCell className="text-right num text-sm" data-testid={`visit-leads-${r.visit_id}`}>{r.loan_leads_verified}/{r.loan_leads_count}</TableCell>}
                  <TableCell className="text-right num text-sm" data-testid={`visit-photos-${r.visit_id}`}>{r.photo_count}{r.verification_photos ? <span className="text-slate-400"> +{r.verification_photos}</span> : null}</TableCell>
                  <TableCell className="text-right num" data-testid={`visit-valuation-${r.visit_id}`}>
                    {r.valuation_amount_inr != null ? Number(r.valuation_amount_inr).toLocaleString("en-IN") : "-"}
                    {(r.plot_value_inr != null || r.construction_value_inr != null) && (
                      <div className="text-[11px] text-slate-500 whitespace-nowrap">
                        {r.plot_value_inr != null ? `Plot ${Number(r.plot_value_inr).toLocaleString("en-IN")}` : ""}{r.plot_value_inr != null && r.construction_value_inr != null ? " + " : ""}{r.construction_value_inr != null ? `Constr ${Number(r.construction_value_inr).toLocaleString("en-IN")}` : ""}
                        {r.valuation_mode === "Manual" ? " · manual" : ""}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={showLoanLeads ? 11 : 10} className="text-center text-slate-500 py-8" data-testid="visits-empty">No visits found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
